import $ from 'jquery';
import { defaultHooks } from '@wordpress/hooks';
import { select, dispatch } from '@wordpress/data';
import {
	isPayoneerGatewaySelected,
	focusOnSelectedGateway
} from '../woocommerce';
import { PAYONEER_STORE_KEY } from '../../store';
import { setupClassicCheckoutListeners } from '../../store/storeListeners';
import OrderPlacementManager from '../OrderPlacementManager';

const log = ( ...args ) => console.log( '[classic checkout]', ...args );

const initPayoneerComponents = () => dispatch( PAYONEER_STORE_KEY ).initPayoneerComponents();
const payWithPayoneer = () => dispatch( PAYONEER_STORE_KEY ).pay();
const rebootPayoneer = () => dispatch( PAYONEER_STORE_KEY ).reboot();
const setOnPaymentUnsuccessfulNonce = () => dispatch( PAYONEER_STORE_KEY ).setOnPaymentUnsuccessfulNonce();
const releaseOnHoldOrder = ( requestData ) => dispatch( PAYONEER_STORE_KEY ).releaseOnHoldOrder( requestData );
const payoneerStore = select( PAYONEER_STORE_KEY );

export const initClassicCheckout = () => {
	log( 'init' );

	const orderPlacementManager = new OrderPlacementManager( '#payment' );

	/**
	 * Set up hooks before everything else to ensure that we already capture
	 * actions that are fired during the following setup steps.
	 */

	defaultHooks.addAction(
		'payoneer.checkout.paymentMethodChanged',
		'payoneer/shortcodeCheckout',
		( { key, isValid, isReady } ) => {
			log( 'hook paymentMethodChanged' );

			if ( !key && !isPayoneerGatewaySelected() ) {
				// The method name is only set when a payoneer component is active.
				orderPlacementManager.setActive( false );
				return;
			}

			orderPlacementManager.setActive( true );
			orderPlacementManager.setProcessing( !isReady );
			orderPlacementManager.setValidInput( isValid );
		}
	);

	const $cancelButton = $( '<button type="button" />' )
		.text( 'Cancel Payment' )
		.on( 'click', () => {
			// Hide the button and display the spinner.
			orderPlacementManager.setProcessingMessage( null );

			releaseOnHoldOrder( { longId: payoneerStore.getLongId() } )
				.then( () => window.location.reload() );
		} );

	const showCancelButton = ( paymentMethod ) => {
		if ( 'cards' === paymentMethod ) {
			return;
		}

		let testCancelButton = false;
		try {
			// Enable cancel button (unstable): localStorage.setItem( 'testCancelButton', true );
			testCancelButton = localStorage.getItem( 'testCancelButton' );
		} catch ( e ) {
		}

		if ( !testCancelButton ) {
			return;
		}

		// Replace the spinner with a cancel button, after a short delay.
		setTimeout( () => orderPlacementManager.setProcessingMessage( $cancelButton ), 2000 );
		// Hide the cancel button after 57 seconds, because the payment auto-completes after 60 seconds.
		setTimeout( () => orderPlacementManager.setProcessingMessage( null ), 57000 );
	};

	document.addEventListener(
		'DOMContentLoaded',
		() => {
			log( 'dom ready, setup listeners' );

			setupClassicCheckoutListeners();
			setOnPaymentUnsuccessfulNonce( PayoneerData.onPaymentUnsuccessfulNonce );

			const $checkoutForm = $( 'form.checkout' );

			defaultHooks.addFilter(
				'payoneer.webSdk.onSdkError',
				'payoneer/shortcodeCheckout',
				( isResolved, checkout, component, data ) => {
					log( 'hook onSdkError', { isResolved, data } );

					rebootPayoneer();

					$( document.body ).trigger( 'update_checkout' );
					return isResolved;
				}
			);

			defaultHooks.addFilter(
				'payoneer.webSdk.onPaymentDeclined',
				'payoneer/shortcodeCheckout',
				(continueDefaultErrorHandling, sdk, componentName, responseData ) => {
					log( 'hook onPaymentDeclined' );

					handleUnsuccessfulPayment(responseData, 'declined');
					return true;
				}
			);

			defaultHooks.addFilter(
				'payoneer.webSdk.onPaymentFailure',
				'payoneer/shortcodeCheckout',
				(continueDefaultErrorHandling, sdk, componentName, responseData ) => {
					log( 'hook onPaymentFailure' );

					handleUnsuccessfulPayment(responseData, 'failure');
					return true;
				}
			);

			/**
			 * Cannot use native event listener for events dispatched by jQuery
			 * https://github.com/jquery/jquery/issues/3347
			 */
			$( document.body ).on( 'updated_checkout', () => {
				log( 'event updated_checkout' );

				initPayoneerComponents();
			} );

			$checkoutForm.on( 'checkout_place_order', ( e ) => {
				log( 'event checkout_place_order' );

				if ( !isPayoneerGatewaySelected() || payoneerStore.isHostedModeForced() ) {
					return;
				}

				// Prevent submission if blocked
				if ( !orderPlacementManager.isPaymentAllowed ) {
					e.preventDefault();
					e.stopImmediatePropagation();
					log( 'checkout submission prevented - payment not allowed in current state' );
					return false;
				}

				const { isValid, isReady } = payoneerStore.getActiveComponent();

				if ( !isReady || !isValid ) {
					try {
						focusOnSelectedGateway();
					} catch ( error ) {
						console.error( error );
					}

					return false;
				}

				// Block the button before submitting the form.
				orderPlacementManager.setProcessing( true );

				return true;
			} );

			$checkoutForm.on( 'checkout_place_order_success', ( e, result ) => {
				log( 'event checkout_place_order_success' );

				if ( !isPayoneerGatewaySelected() || payoneerStore.isHostedModeForced() ) {
					return;
				}

				const { key } = payoneerStore.getActiveComponent();

				payWithPayoneer();
				showCancelButton( key );

				// Override default redirect to hosted payment page.
				result.redirect = '#payoneer-redirect';

				return false;
			} );

			// Force allow payment on checkout errors (bypass component state)
			$( document.body ).on( 'checkout_error', ( event, msg ) => {
				if ( msg && msg.includes( 'data-payment-state="pending"' ) ) {
					/**
					 * This is not a checkout error, but a workaround for WooCommerce:
					 *
					 * The order was created on server side, but the payment was not completed yet.
					 * Since the Woo-flow expects synchronous payments, we signal "success" as API
					 * response, but our `checkout_place_order_success` handler returns false,
					 * leaving WooCommerce with mixed signals, and triggering this "faux-error".
					 * We can ignore it, as payment is currently processing via the WebSDK.
					 */
					return;
				}

				log( 'event checkout_error' );

				orderPlacementManager.setProcessing( false );
			} );
		},
		false
	);

	const handleUnsuccessfulPayment = ( responseData, paymentResult ) => {
		releaseOnHoldOrder( {
			longId: payoneerStore.getLongId(),
			errorTitleToDisplay: responseData.interaction?.customerMessage?.title ?? 'Payment failed',
			errorTextToDisplay: responseData.interaction?.customerMessage?.text ?? 'Please try again.',
			paymentResult
		} ).finally( () => {
			// Always unblock after payment decline is handled.
			orderPlacementManager.setProcessing( false );
			$( document.body ).trigger( 'update_checkout' );
		} );
	}
};
